import React, { useRef } from 'react';

import lowerCase from 'lodash/lowerCase';

import { useClassnames } from '@lumapps/classnames';
import { Orientation, FlexBox, Alignment, Theme, Size } from '@lumapps/lumx/react';
import { VoteButton } from '@lumapps/reactions-votes/components/VoteButton';
import { useElementSize } from '@lumapps/responsive/hooks/useElementSize';

import { IdeaVote, PostVotes } from '../../../types';
import { usePostVote } from './usePostVote';

interface PostVoteProps {
    theme?: Theme;
    initialVotes: PostVotes;
    postId: string;
    isPreviewOnly?: boolean;
}

const CLASSNAME = 'block-post-vote';
const SCOPE = 'post-vote';
const COMPACT_BREAKPOINT = 400;

const PostVote: React.FC<PostVoteProps> = ({ initialVotes, theme, postId, isPreviewOnly }) => {
    const { element, block } = useClassnames(CLASSNAME);
    const ref = useRef(null);

    const { width } = useElementSize(ref);

    const {
        onVote,
        onDeleteVote,
        votes: { downVotes, upVotes, userVote },
    } = usePostVote({
        postId,
        initialVotes,
    });

    const handleVote = (vote: IdeaVote) => {
        // If current vote is similar as previous one -> delete
        if (lowerCase(userVote) === lowerCase(vote)) {
            onDeleteVote();
        } else {
            onVote(vote);
        }
    };

    const localVote = userVote?.toLowerCase();
    const isCompact = (width || 0) < COMPACT_BREAKPOINT;

    const voteBtnProps = {
        theme,
        isCompact,
        isPreviewOnly,
        scope: SCOPE,
    };

    return (
        <FlexBox
            theme={theme}
            orientation={Orientation.horizontal}
            vAlign={Alignment.right}
            className={block()}
            gap={Size.regular}
            fillSpace
            ref={ref}
        >
            <VoteButton
                {...voteBtnProps}
                voteCount={upVotes}
                handleVote={() => handleVote(IdeaVote.UP)}
                isSelected={localVote === IdeaVote.UP}
                className={element('up-btn')}
                action="upvote"
            />
            <VoteButton
                {...voteBtnProps}
                voteCount={downVotes}
                handleVote={() => handleVote(IdeaVote.DOWN)}
                isSelected={localVote === IdeaVote.DOWN}
                className={element('down-btn')}
                action="downvote"
            />
        </FlexBox>
    );
};

export { PostVote };
